import { useCallback, useEffect, useState } from "react";
import { Wallet, Search, RefreshCw, AlertTriangle, ChevronLeft, ChevronRight, X } from "lucide-react";
import adminService from "../../../../services/adminService";
import WalletBalances from "./WalletBalances";
import "./AdminFinance.scss";

const TYPE_TABS = [
  { key: "all", label: "Tudo" },
  { key: "deposits", label: "Depósitos" },
  { key: "withdrawals", label: "Saques" },
  { key: "jobs", label: "Vagas" },
];

const TYPE_LABELS = {
  Deposit: "Depósito",
  Withdrawal: "Saque",
  JobReserved: "Reserva de vaga",
  JobPayment: "Pagamento de vaga",
  Refund: "Reserva liberada",
  PlatformFee: "Taxa da plataforma",
  Adjustment: "Ajuste",
};

const STATUS_LABELS = {
  completed: "Concluído",
  pending: "Pendente",
  failed: "Falhou",
  cancelled: "Cancelado",
};

const USER_TYPE_LABELS = {
  Company: "Empresa",
  Freelancer: "Freelancer",
  Admin: "Admin",
};

function formatCurrency(value) {
  return (value ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDateTime(value) {
  return new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminFinance() {
  const [view, setView] = useState("transactions");
  const [selectedUser, setSelectedUser] = useState(null);
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [stuck, setStuck] = useState(null);
  const [stuckLoading, setStuckLoading] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminService.getWalletTransactions({
        type,
        search: appliedSearch || undefined,
        from: from || undefined,
        to: to || undefined,
        userId: selectedUser?.userId,
        page,
      });
      setData(result);
    } catch (error) {
      console.error("Erro ao carregar histórico financeiro:", error);
    } finally {
      setLoading(false);
    }
  }, [type, appliedSearch, from, to, page, selectedUser]);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await adminService.getWalletsSummary());
    } catch (error) {
      console.error("Erro ao carregar resumo das carteiras:", error);
    }
  }, []);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const changeType = (key) => {
    setType(key);
    setPage(1);
  };

  const checkStuck = async () => {
    setStuckLoading(true);
    try {
      setStuck(await adminService.getStuckReservations());
    } catch (error) {
      alert(error?.response?.data?.message || "Erro ao verificar reservas travadas.");
    } finally {
      setStuckLoading(false);
    }
  };

  const releaseStuck = async () => {
    const total = stuck.reduce((sum, s) => sum + s.amount, 0);
    const confirmed = window.confirm(
      `Devolver ${formatCurrency(total)} para ${stuck.length} vaga(s)? ` +
      "O valor volta a ficar disponível na carteira de cada empresa."
    );
    if (!confirmed) return;

    setReleasing(true);
    try {
      const result = await adminService.releaseStuckReservations();
      alert(`${formatCurrency(result.totalReleased)} devolvidos em ${result.count} vaga(s).`);
      setStuck(null);
      loadSummary();
      loadTransactions();
    } catch (error) {
      alert(error?.response?.data?.message || "Erro ao devolver as reservas.");
    } finally {
      setReleasing(false);
    }
  };

  // Clique num usuário da lista de saldos: abre o extrato dele
  const openUserStatement = (user) => {
    setSelectedUser({ userId: user.userId, userName: user.userName });
    setType("all");
    setPage(1);
    setView("transactions");
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="admin-finance">
      <div className="admin-finance__header">
        <h2>
          <Wallet size={18} />
          Financeiro
        </h2>
        <button className="admin-finance__refresh" onClick={() => { loadSummary(); loadTransactions(); }}>
          <RefreshCw size={14} />
          Atualizar
        </button>
      </div>

      {summary && (
        <div className="admin-finance__summary">
          <div className="admin-finance__card">
            <span>Total nas carteiras</span>
            <strong>{formatCurrency(summary.totalBalance)}</strong>
            <small>Deve bater com o saldo da conta Asaas</small>
          </div>
          <div className="admin-finance__card">
            <span>Reservado em vagas e saques</span>
            <strong>{formatCurrency(summary.totalReserved)}</strong>
          </div>
          <div className="admin-finance__card">
            <span>Saques em andamento</span>
            <strong>{formatCurrency(summary.pendingWithdrawals)}</strong>
          </div>
          {summary.byUserType.map((b) => (
            <div className="admin-finance__card" key={b.userType}>
              <span>{USER_TYPE_LABELS[b.userType] ?? b.userType}</span>
              <strong>{formatCurrency(b.balance)}</strong>
              <small>{b.count} carteira(s) com saldo</small>
            </div>
          ))}
        </div>
      )}

      <div className="admin-finance__stuck">
        <div className="admin-finance__stuck-head">
          <div>
            <strong>Reservas travadas</strong>
            <p>Valores reservados em vagas que expiraram, foram encerradas ou apagadas sem ninguém trabalhar.</p>
          </div>
          <button onClick={checkStuck} disabled={stuckLoading}>
            {stuckLoading ? "Verificando..." : "Verificar"}
          </button>
        </div>

        {stuck && stuck.length === 0 && (
          <p className="admin-finance__stuck-ok">Nenhuma reserva travada.</p>
        )}

        {stuck && stuck.length > 0 && (
          <>
            <div className="admin-finance__stuck-list">
              {stuck.map((s) => (
                <div className="admin-finance__stuck-row" key={`${s.companyId}-${s.jobId}`}>
                  <AlertTriangle size={14} />
                  <span className="admin-finance__strong">{s.companyName}</span>
                  <span>{s.jobTitle}</span>
                  <span>{s.jobDate ? new Date(s.jobDate).toLocaleDateString("pt-BR") : "—"}</span>
                  <span className="admin-finance__strong">{formatCurrency(s.amount)}</span>
                </div>
              ))}
            </div>
            <button
              className="admin-finance__release"
              onClick={releaseStuck}
              disabled={releasing}
            >
              {releasing ? "Devolvendo..." : "Devolver às empresas"}
            </button>
          </>
        )}
      </div>

      <div className="admin-finance__views">
        <button
          className={view === "transactions" ? "active" : ""}
          onClick={() => setView("transactions")}
        >
          Movimentações
        </button>
        <button
          className={view === "balances" ? "active" : ""}
          onClick={() => setView("balances")}
        >
          Saldos por usuário
        </button>
      </div>

      {view === "balances" && <WalletBalances onSelectUser={openUserStatement} />}

      {view === "transactions" && (
      <>
      {selectedUser && (
        <div className="admin-finance__user-filter">
          Extrato de <strong>{selectedUser.userName}</strong>
          <button onClick={() => { setSelectedUser(null); setPage(1); }} title="Limpar filtro">
            <X size={14} />
          </button>
        </div>
      )}

      <div className="admin-finance__filters">
        <div className="admin-finance__tabs">
          {TYPE_TABS.map((t) => (
            <button
              key={t.key}
              className={type === t.key ? "active" : ""}
              onClick={() => changeType(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <form className="admin-finance__search" onSubmit={handleSearch}>
          <Search size={14} />
          <input
            placeholder="Nome ou e-mail"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </form>

        <div className="admin-finance__dates">
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
          <span>até</span>
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
        </div>
      </div>

      {data && (
        <div className="admin-finance__totals">
          <span>Depósitos concluídos: <strong>{formatCurrency(data.totals.deposits)}</strong></span>
          <span>Saques concluídos: <strong>{formatCurrency(data.totals.withdrawals)}</strong></span>
          <span>{data.total} movimentação(ões)</span>
        </div>
      )}

      <div className="admin-finance__table">
        <div className="admin-finance__head">
          <span>Data</span>
          <span>Usuário</span>
          <span>Tipo</span>
          <span>Valor</span>
          <span>Status</span>
          <span>Detalhes</span>
        </div>

        {loading && <div className="admin-loading">Carregando movimentações...</div>}

        {!loading && data?.items.length === 0 && (
          <div className="admin-finance__empty">Nenhuma movimentação encontrada.</div>
        )}

        {!loading && data?.items.map((item) => (
          <div className="admin-finance__row" key={item.id}>
            <span>{formatDateTime(item.createdAt)}</span>

            <span title={item.userEmail}>
              <span className="admin-finance__strong">{item.userName}</span>
              <small>{USER_TYPE_LABELS[item.userType] ?? item.userType}</small>
            </span>

            <span>{TYPE_LABELS[item.type] ?? item.type}</span>

            <span
              className={
                !item.affectsBalance
                  ? "admin-finance__amount admin-finance__amount--neutral"
                  : item.amount > 0
                    ? "admin-finance__amount admin-finance__amount--in"
                    : "admin-finance__amount admin-finance__amount--out"
              }
              title={!item.affectsBalance ? "Não altera o saldo total, só o valor reservado" : undefined}
            >
              {item.affectsBalance && item.amount > 0 ? "+" : ""}
              {formatCurrency(item.amount)}
            </span>

            <span className={`admin-finance__status admin-finance__status--${item.status}`}>
              {STATUS_LABELS[item.status] ?? item.status}
            </span>

            <span className="admin-finance__details" title={item.description}>
              {item.jobTitle && <span>Vaga: {item.jobTitle}</span>}
              {item.pixKey && <span>Pix ({item.pixKeyType}): {item.pixKey}</span>}
              {item.asaasId && <small>Asaas: {item.asaasId}</small>}
              {!item.jobTitle && !item.pixKey && <span>{item.description}</span>}
            </span>
          </div>
        ))}
      </div>

      {data && totalPages > 1 && (
        <div className="admin-finance__pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <ChevronLeft size={14} />
          </button>
          <span>Página {page} de {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            <ChevronRight size={14} />
          </button>
        </div>
      )}
      </>
      )}
    </div>
  );
}
